<template>
    <div class="kata-bracket">
        <!-- Desktop: table -->
        <table class="table is-striped is-hoverable is-fullwidth is-hidden-touch">
            <thead>
                <tr>
                    <th>#</th>
                    <th>{{ translate("common.competitor") }}</th>
                    <th>{{ translate("common.academy") }}</th>
                    <th>{{ translate("page.public_brackets.kata_scores") }}</th>
                    <th>{{ translate("scoreboard.kata_total_score") }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(entry, index) in entries" :key="index">
                    <td>{{ entry.place ? "#" + entry.place : "-" }}</td>
                    <td>
                        <a v-if="entry.publicId" class="clarity-mask" @click="openProfile(entry.publicId)">
                            {{ entry.firstName }} {{ entry.lastName }}
                        </a>
                        <span v-else class="clarity-mask">{{ entry.firstName }} {{ entry.lastName }}</span>
                    </td>
                    <td>{{ entry.academy }}</td>
                    <td>
                        <template v-if="hasMultiplePerformances(entry)">
                            <div v-for="(perf, pi) in entry.performances" :key="pi">
                                <span class="is-size-7 has-text-grey-light mr-1">P{{ pi + 1 }}</span>
                                <template v-if="perf.judgeScores.length > 0">
                                    <span
                                        v-for="(score, si) in getSortedScores(perf.judgeScores)"
                                        :key="si"
                                        class="kata-judge-score"
                                        :class="{
                                            'kata-judge-score--dropped': isDropped(
                                                getSortedScores(perf.judgeScores),
                                                si,
                                                perf.discardMinMax,
                                            ),
                                        }"
                                    >
                                        {{ score.toFixed(1) }}
                                    </span>
                                </template>
                                <span v-else class="is-size-7 has-text-grey-light">—</span>
                            </div>
                        </template>
                        <template v-else-if="entry.judgeScores.length > 0">
                            <span
                                v-for="(score, index2) in getSortedScores(entry.judgeScores)"
                                :key="index2"
                                class="kata-judge-score"
                                :class="{
                                    'kata-judge-score--dropped': isDropped(getSortedScores(entry.judgeScores), index2, entry.discardMinMax),
                                }"
                            >
                                {{ score.toFixed(1) }}
                            </span>
                        </template>
                        <span v-else>-</span>
                    </td>
                    <td>
                        <strong v-if="entry.totalScore !== null">{{ entry.totalScore.toFixed(1) }}</strong>
                        <span v-else>-</span>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Mobile: boxes -->
        <div class="is-hidden-desktop">
            <div v-for="(entry, index) in entries" :key="index" class="box kata-entry">
                <div class="columns is-mobile is-vcentered">
                    <div class="column is-narrow">
                        <span class="tag is-medium">
                            {{ entry.place ? "#" + entry.place : "-" }}
                        </span>
                    </div>

                    <div class="column">
                        <a v-if="entry.publicId" class="clarity-mask" @click="openProfile(entry.publicId)">
                            {{ entry.firstName }} {{ entry.lastName }}
                        </a>
                        <span v-else class="clarity-mask">{{ entry.firstName }} {{ entry.lastName }}</span>

                        <div class="has-text-grey-light">{{ entry.academy }}</div>

                        <div v-if="hasMultiplePerformances(entry)" class="has-text-grey-light mt-1">
                            <div v-for="(perf, pi) in entry.performances" :key="pi">
                                <span class="has-text-white is-size-7 mr-1">P{{ pi + 1 }}</span>
                                <template v-if="perf.judgeScores.length > 0">
                                    <span
                                        v-for="(score, si) in getSortedScores(perf.judgeScores)"
                                        :key="si"
                                        class="kata-judge-score"
                                        :class="{
                                            'kata-judge-score--dropped': isDropped(
                                                getSortedScores(perf.judgeScores),
                                                si,
                                                perf.discardMinMax,
                                            ),
                                        }"
                                    >
                                        {{ score.toFixed(1) }}
                                    </span>
                                </template>
                                <span v-else class="is-size-7">—</span>
                            </div>
                        </div>
                        <div v-else-if="entry.judgeScores.length > 0" class="has-text-grey-light mt-1">
                            <span class="has-text-white">{{ translate("page.public_brackets.kata_scores") }}:</span>
                            <span
                                v-for="(score, index2) in getSortedScores(entry.judgeScores)"
                                :key="index2"
                                class="kata-judge-score"
                                :class="{
                                    'kata-judge-score--dropped': isDropped(getSortedScores(entry.judgeScores), index2, entry.discardMinMax),
                                }"
                            >
                                {{ score.toFixed(1) }}
                            </span>
                        </div>
                    </div>

                    <div class="column is-narrow has-text-centered">
                        <div class="is-size-4 has-text-weight-bold">
                            {{ entry.totalScore !== null ? entry.totalScore.toFixed(1) : "-" }}
                        </div>
                        <div class="is-size-7 has-text-grey-light">
                            {{ translate("page.admin_academy_results.points") }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { translate } from "../Common/translations";
    import { eventBus, OPEN_RECORD_PROFILE } from "./publicBracketEventBus";
    import { getSortedScores, isDropped, KataEntry } from "../Common/Kata/useKataScores";

    defineProps<{
        entries: Array<KataEntry>;
    }>();

    function openProfile(publicId: string): void {
        eventBus.$emit(OPEN_RECORD_PROFILE, { publicId });
    }

    function hasMultiplePerformances(entry: KataEntry): boolean {
        return (entry.performances?.length ?? 0) > 1;
    }
</script>
